import React from 'react';
import { useLocation } from 'react-router';
import { ButtonMinorAction } from '../../../../components/molecules/Btn/ButtonWrapper';
import { Icons } from '../../../../components/atoms/SVG';
import { TooltipPlacement } from '../../../../components/atoms/Tooltip';
import { ContextMenu, ContextMenuButton } from '../../ContextMenu';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { PostForm, PostReplyForm, PostTypes } from '../Post.types';
import { useRefreshBlock } from '../../../BlockFetcher';
import { ContextMenuLink } from '../../ContextMenu/partials/ContextMenuLink';
import { PostFlagInappropriateForm } from '../forms/PostFlagInappropriateModal';
import { PostDeleteForm } from '../forms/PostDeleteModal';
import { PostUpdateForm } from '../forms/PostUpdateForm';

type PostInteractions = {
  postId: string;
  replyForm?: PostReplyForm | undefined;
  updateForm?: PostUpdateForm;
  deleteForm?: PostDeleteForm;
  flagInappropriateForm?: PostFlagInappropriateForm;
  currentForm: PostForm | undefined;
  setCurrentForm: (newState: PostForm | undefined) => void;
  type: PostTypes;
  editEditorId: string;
  replyEditorId?: string;
  postNumber: number | string;
};

export function PostsInteractions({
  postId,
  replyForm,
  updateForm,
  deleteForm,
  flagInappropriateForm,
  setCurrentForm,
  currentForm,
  type,
  editEditorId,
  replyEditorId,
  postNumber,
}: PostInteractions) {
  const { t } = useSonTranslation('Posts');
  const { hash } = useLocation();
  const { refresh } = useRefreshBlock();

  const handleReplyClick = () => {
    if (currentForm === replyForm) {
      setCurrentForm(undefined);
    } else {
      setCurrentForm(replyForm);
    }
  };

  return (
    <div className="posts__interactions">
      {(replyForm || updateForm || deleteForm || flagInappropriateForm) && (
        <>
          {replyEditorId && replyForm && type !== 'reply' && (
            <ButtonMinorAction
              id={`reply-button-${postId}`}
              icon={Icons.REPLY_MESSAGE}
              text={t(`default.replyButton.${type}`, { postNumber })}
              href={`#reply-editor-${postId}`}
              ariaDisabled={hash.includes(replyEditorId) && currentForm && currentForm === replyForm}
              disabledLink={hash.includes(replyEditorId) && currentForm && currentForm === replyForm}
              onClick={() => handleReplyClick()}
              tooltipPlacement={TooltipPlacement.TOP}
            />
          )}
          {((!deleteForm && updateForm && currentForm !== updateForm) ||
            (deleteForm && updateForm && currentForm !== updateForm) ||
            (deleteForm && !updateForm) ||
            (flagInappropriateForm && !updateForm && !deleteForm)) && (
            <ContextMenu
              tooltipText={t(`default.contextMenu.tooltip.title.${type}`, { postNumber })}
              tooltipPlacement={TooltipPlacement.TOP}
            >
              {updateForm && (
                <ContextMenuLink
                  href={`#edit-editor-${postId}`}
                  routing={false}
                  onClick={() => setCurrentForm(updateForm)}
                >
                  {t(`default.contextMenu.edit.title.${type}`)}
                </ContextMenuLink>
              )}
              {deleteForm && (
                <ContextMenuButton aria-haspopup onClick={() => setCurrentForm(deleteForm)}>
                  {t(`default.contextMenu.delete.${type}.title`)}
                </ContextMenuButton>
              )}
              {flagInappropriateForm && (
                <ContextMenuButton aria-haspopup onClick={() => setCurrentForm(flagInappropriateForm)}>
                  {t(`default.contextMenu.flagInappropriate.${type}.title`)}
                </ContextMenuButton>
              )}
            </ContextMenu>
          )}
          {hash.includes(editEditorId) && currentForm === updateForm && (
            <ButtonMinorAction
              active
              icon={Icons.DECLINE_CROSS}
              text={t('default.stopEditing')}
              href={`#post-${postId}`}
              onClick={() => {
                setCurrentForm(undefined);

                // only threads contain attachedFiles, which are connected to WS in edit mode.
                // default mode will not receive updates -> manually refresh when closing.
                // see SIBZIEL-13036
                if (type === 'thread') {
                  refresh();
                }
              }}
              tooltipPlacement={TooltipPlacement.TOP}
            />
          )}
        </>
      )}
    </div>
  );
}
